import React, { useContext } from 'react';
import clsx from 'clsx';
import { ItemProps, LinkDOMProps } from '@react-types/shared';
import {
  MenuItem as AriaMenuItem,
  MenuItemProps as AriaMenuItemProps,
} from 'react-aria-components';

import styles from '@danske/sapphire-css/components/listbox/listbox.module.css';
import { Icon } from '../../Icon';
import { V5MenuCompatibilityContext } from './v5Compatibility';
import { Checkmark } from '@danske/sapphire-icons/react';

export interface MenuItemProps<T>
  extends Pick<
    AriaMenuItemProps,
    | 'id'
    | 'textValue'
    | 'aria-label'
    | 'children'
    | 'isDisabled'
    | keyof LinkDOMProps
  > {
  /**
   * @deprecated Unused prop. Will be removed in the next major version.
   */
  hasChildItems?: ItemProps<T>['hasChildItems'];
  /**
   * @deprecated Unused prop. Will be removed in the next major version.
   */
  childItems?: ItemProps<T>['childItems'];
  /**
   * @deprecated Will be removed in the next major version.
   */
  title?: ItemProps<T>['title'];

  /**
   * Menu item icon
   * @deprecated use `rightIcon` instead.
   */
  icon?: React.ReactNode;

  /**
   * An icon rendered on the right side of the menu item.
   */
  rightIcon?: React.ReactNode;

  /**
   * An icon rendered on the left side of the menu item.
   */
  leftIcon?: React.ReactNode;

  /**
   * A secondary text rendered as an extra, less-emphasized line.
   */
  secondaryText?: React.ReactNode;
  /**
   * Whether the action is a dangerous.
   */
  isDangerous?: boolean;

  /**
   * Handler that is called when the item is selected.
   * */
  onAction?: () => void;

  /**
   * **Not recommended** – use onAction instead.
   * onClick is provided only for compatibility with other libraries.
   */
  onClick?: AriaMenuItemProps['onClick'];
}

export function MenuItem<T>({
  icon,
  rightIcon = icon,
  leftIcon,
  ...props
}: MenuItemProps<T>): React.JSX.Element {
  const { dangerKeys } = useContext(V5MenuCompatibilityContext);
  const isDanger =
    props.isDangerous ?? [...(dangerKeys || [])].includes(props.id!);
  return (
    <AriaMenuItem
      {...props}
      textValue={
        props.textValue ||
        (typeof props.children === 'string' ? props.children : undefined)
      }
      className={({ isHovered, isDisabled, isFocusVisible, isPressed }) =>
        clsx(
          styles['sapphire-listbox__item'],
          styles['js-focus'],
          styles['js-hover'],
          {
            [styles['is-disabled']]: isDisabled,
            [styles['is-focus']]: isFocusVisible,
            [styles['is-hover']]: isHovered,
            [styles['is-active']]: isPressed,
            [styles['sapphire-listbox__item--danger']]: isDanger,
          }
        )
      }
    >
      {({ isSelected }) => (
        <div className={clsx(styles['sapphire-listbox__content'])}>
          {isSelected && (
            <div className={clsx(styles['sapphire-listbox__checkmark'])}>
              <Icon size="sm" color="informative">
                <Checkmark />
              </Icon>
            </div>
          )}
          {leftIcon && (
            <div className={clsx(styles['sapphire-listbox__icon'])}>
              <Icon size="sm">{leftIcon}</Icon>
            </div>
          )}
          <div className={clsx(styles['sapphire-listbox__text-container'])}>
            <div className={clsx(styles['sapphire-listbox__primary-text'])}>
              {props.children}
            </div>
            {props.secondaryText && (
              <div className={clsx(styles['sapphire-listbox__secondary-text'])}>
                {props.secondaryText}
              </div>
            )}
          </div>
          {rightIcon && (
            <div className={clsx(styles['sapphire-listbox__icon'])}>
              <Icon size="sm">{rightIcon}</Icon>
            </div>
          )}
        </div>
      )}
    </AriaMenuItem>
  );
}
